import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE } from '@/constants/pageSize';
import StyledTableActionsRow from '@/components/styled/StyledTableActionsRow';

export interface useStyledTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  sorting?: SortingState;
  setSorting?: React.Dispatch<React.SetStateAction<SortingState>>;
  rowSelection?: RowSelectionState;
  getRowId?: (
    originalRow: TData,
    index: number,
    parent?: Row<TData> | undefined,
  ) => string;
  setSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  defaultRowSelection?: RowSelectionState;
  onDeleteRow?: (row: Row<TData>) => void;
  onDuplicateRow?: (row: Row<TData>) => void;
  duplicateEnablePredicate?: (row: Row<TData>) => boolean;
  onViewRow?: (row: Row<TData>) => void;
  onEditRow?: (row: Row<TData>) => void;
  enablePagination?: boolean;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  isExpandable?: boolean;
  onAcceptRow?: (row: Row<TData>) => void;
  onRefuseRow?: (row: Row<TData>) => void;
  initialPageSize?: number;
  onAddRow?: (row: Row<TData>) => void;
  onCancelRow?: (row: Row<TData>) => void;
  onShareRow?: (row: Row<TData>) => void;
  shareEnablePredicate?: (row: Row<TData>) => boolean;
  onListRow?: (row: Row<TData>) => void;
  listEnablePredicate?: (row: Row<TData>) => boolean;
  acceptRefuseEnablePredicate?: (row: Row<TData>) => boolean;
  editEnablePredicate?: (row: Row<TData>) => boolean;
  onRestoreRow?: (row: Row<TData>) => void;
  alertMessage?: string;
  alertEnablePredicate?: (row: Row<TData>) => boolean;
  onAlertClick?: (row: Row<TData>) => void;
  onDownloadRow?: (row: Row<TData>) => void;
  clientSideSorting?: boolean;
}

export const useStyledTable = <TData, TValue>({
  columns,
  data,
  sorting,
  setSorting,
  rowSelection: rowSelectionProp,
  getRowId,
  setSelection,
  onDeleteRow,
  onDuplicateRow,
  onViewRow,
  onEditRow,
  duplicateEnablePredicate,
  enablePagination = true,
  getRowCanExpand,
  isExpandable = false,
  defaultRowSelection,
  onAcceptRow,
  onRefuseRow,
  initialPageSize,
  onAddRow,
  onCancelRow,
  onShareRow,
  shareEnablePredicate,
  onListRow,
  listEnablePredicate,
  acceptRefuseEnablePredicate,
  editEnablePredicate,
  onRestoreRow,
  alertMessage,
  alertEnablePredicate,
  onAlertClick,
  onDownloadRow,
  clientSideSorting = false,
}: useStyledTableProps<TData, TValue>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionProp ?? defaultRowSelection ?? {},
  );

  useEffect(() => {
    if (rowSelectionProp !== undefined) setRowSelection(rowSelectionProp);
  }, [rowSelectionProp]);
  const columnsWithActions = useMemo(
    () => [
      ...columns,
      {
        id: 'actions',
        cell: ({ row }) => (
          <StyledTableActionsRow
            onDelete={onDeleteRow ? () => onDeleteRow(row) : undefined}
            onEdit={onEditRow ? () => onEditRow(row) : undefined}
            onDuplicate={onDuplicateRow ? () => onDuplicateRow(row) : undefined}
            onView={onViewRow ? () => onViewRow(row) : undefined}
            onAccept={onAcceptRow ? () => onAcceptRow(row) : undefined}
            onRefuse={onRefuseRow ? () => onRefuseRow(row) : undefined}
            duplicateEnablePredicate={
              duplicateEnablePredicate
                ? () => duplicateEnablePredicate(row)
                : undefined
            }
            onExpand={
              isExpandable && row.getCanExpand() && !row.getIsExpanded()
                ? () => row.toggleExpanded()
                : undefined
            }
            onReduce={
              isExpandable && row.getCanExpand() && row.getIsExpanded()
                ? () => row.toggleExpanded()
                : undefined
            }
            onAdd={onAddRow ? () => onAddRow(row) : undefined}
            onCancel={onCancelRow ? () => onCancelRow(row) : undefined}
            onShare={onShareRow ? () => onShareRow(row) : undefined}
            shareEnablePredicate={
              shareEnablePredicate ? () => shareEnablePredicate(row) : undefined
            }
            onList={onListRow ? () => onListRow(row) : undefined}
            listEnablePredicate={
              listEnablePredicate ? () => listEnablePredicate(row) : undefined
            }
            acceptRefuseEnablePredicate={
              acceptRefuseEnablePredicate
                ? () => acceptRefuseEnablePredicate(row)
                : undefined
            }
            editEnablePredicate={
              editEnablePredicate ? () => editEnablePredicate(row) : undefined
            }
            onRestore={onRestoreRow ? () => onRestoreRow(row) : undefined}
            alertMessage={alertMessage}
            alertEnablePredicate={
              alertEnablePredicate ? () => alertEnablePredicate(row) : undefined
            }
            onAlertClick={onAlertClick ? () => onAlertClick(row) : undefined}
            onDownload={onDownloadRow ? () => onDownloadRow(row) : undefined}
          />
        ),
      },
    ],
    [
      acceptRefuseEnablePredicate,
      alertEnablePredicate,
      alertMessage,
      columns,
      duplicateEnablePredicate,
      editEnablePredicate,
      isExpandable,
      listEnablePredicate,
      onAcceptRow,
      onAddRow,
      onAlertClick,
      onCancelRow,
      onDeleteRow,
      onDownloadRow,
      onDuplicateRow,
      onEditRow,
      onListRow,
      onRefuseRow,
      onRestoreRow,
      onShareRow,
      onViewRow,
      shareEnablePredicate,
    ],
  );
  const table = useReactTable({
    data,
    columns: columnsWithActions,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    onSortingChange: setSorting,
    manualSorting: !clientSideSorting,
    onRowSelectionChange: (selection) => {
      // Controlled: parent owns rowSelection via rowSelection prop; updating internal
      // state here fights options.state.rowSelection and can leave the UI stale.
      if (rowSelectionProp !== undefined) {
        if (setSelection) setSelection(selection);
      } else {
        setRowSelection(selection);
        if (setSelection) setSelection(selection);
      }
    },
    initialState: enablePagination
      ? {
          pagination: {
            pageSize: initialPageSize ?? PAGE_SIZE,
          },
        }
      : undefined,
    getRowId,
    state: {
      sorting,
      rowSelection: rowSelectionProp ?? rowSelection,
    },
    getRowCanExpand: isExpandable ? getRowCanExpand : undefined,
    getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,
    getSortedRowModel: clientSideSorting ? getSortedRowModel() : undefined,
  });

  return table;
};
